import { createSlice, PayloadAction } from '@reduxjs/toolkit';

// Response shape from GET /api/portfolio/overview
// (mirrors functions/src/firebase/portfolioOverview.ts).
export interface UnitProjection {
  id: string;
  buildingId: number | null;
  currentState: string | string[] | null;
  affordable: boolean;
  actualRent: number | null;
  sqft: number | null;
  numBedrooms: number | null;
  leaseEndDate: string | null;
  actualMoveInDate: string | null;
}

export interface ProcessProjection {
  unitId: string;
  type: string | null;
  status: string;
}

export interface TaskProjection {
  unitId: string;
  dueDate: string;
}

export interface ProjectProjection {
  projectId: string;
  units: UnitProjection[];
  processes: ProcessProjection[];
  tasks: TaskProjection[];
  unscheduledTaskCount: number;
  computedAt: number;
}

export interface PortfolioOverviewResponse {
  projects: ProjectProjection[];
  computedAt: number;
  cached: boolean;
}

export interface PortfolioCacheEntry {
  data: PortfolioOverviewResponse;
  fetchedAt: number;
}

export interface PortfolioState {
  // Keyed by the sorted comma-joined project ids so different portfolios
  // (e.g. after switching org filter) are cached independently.
  byKey: Record<string, PortfolioCacheEntry>;
}

const initialState: PortfolioState = {
  byKey: {},
};

const portfolioSlice = createSlice({
  name: 'portfolio',
  initialState,
  reducers: {
    setPortfolioOverview(
      state,
      action: PayloadAction<{
        key: string;
        data: PortfolioOverviewResponse;
        fetchedAt: number;
      }>
    ) {
      state.byKey[action.payload.key] = {
        data: action.payload.data,
        fetchedAt: action.payload.fetchedAt,
      };
    },
    clearPortfolioOverview(state) {
      state.byKey = {};
    },
  },
});

export const { setPortfolioOverview, clearPortfolioOverview } =
  portfolioSlice.actions;

export default portfolioSlice.reducer;
